import {useState} from "react";
import {Link} from "react-router-dom";
import {App as AntdApp, Alert, Table, Tag, Tooltip} from "antd";
import className from "classnames/bind";
import {Button, FontAwesomeIcon, Loading} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {useAddKeywordItemsMutation, useGetKeywordSiteGapQuery} from "~/reduxs/api/keywordApiSlice";
import ConnectGscCta from "./ConnectGscCta";
import style from "../style/Keyword.module.scss";

const cn = className.bind(style);

const fmt = (v) => Number(v || 0).toLocaleString('vi-VN');

/**
 * Tab "Gap cấp site" (#28): đối chiếu TOP query GSC với các trang ĐÃ CRAWL —
 *  1. Thiếu bài viết   : query có nhu cầu thật nhưng không trang nào có title phủ đủ từ.
 *  2. Thiếu internal link: đã có trang chủ đề nhưng ≤2 link nội bộ trỏ tới
 *     (expand xem gợi ý trang nguồn cùng chủ đề để đặt link).
 * Thuần dữ liệu đã có (GSC + crawl) — khác tab "Phân tích trang" (so 2 URL cụ thể).
 */
function KeywordSiteGap({projectId}) {

	const {notification} = AntdApp.useApp();
	const {data, isLoading} = useGetKeywordSiteGapQuery(projectId);
	const [addItems, {isLoading: saving}] = useAddKeywordItemsMutation();
	const [savingKey, setSavingKey] = useState(null);

	const save = async (r) => {
		try {
			setSavingKey(r.keyword);
			const res = await addItems({
				projectId,
				items: [{keyword: r.keyword, source: 'gsc', impressions: r.impressions, position: r.position, note: 'Content gap: thiếu bài viết'}],
			}).unwrap();
			if (res?.added > 0) notification.success({message: 'Đã lưu vào kho từ khoá'});
			else notification.info({message: 'Từ khoá này đã có trong kho'});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không lưu được từ khoá')});
		} finally {
			setSavingKey(null);
		}
	};

	if (isLoading) return <Loading />;

	if (data && data.connected === false) {
		return <ConnectGscCta projectId={projectId} message="Content gap cấp site đối chiếu truy vấn Google Search Console với các trang đã crawl — dự án này chưa kết nối GSC." />;
	}

	if (data && data.has_crawl === false) {
		return (
			<Alert
				type="info"
				showIcon
				message="Chưa có dữ liệu crawl"
				description={<span>Cần một lần crawl hoàn tất để biết website đang có những trang nào. <Link to={`/projects/${projectId}/crawler`}>Mở Site Crawler</Link>.</span>}
			/>
		);
	}

	const missing = data?.missing || [];
	const weakLink = data?.weak_link || [];

	const metricCols = [
		{title: 'Impressions', dataIndex: 'impressions', key: 'impressions', width: 120, sorter: (a, b) => a.impressions - b.impressions, render: fmt},
		{title: 'Clicks', dataIndex: 'clicks', key: 'clicks', width: 90, responsive: ['md'], render: fmt},
		{title: 'Vị trí', dataIndex: 'position', key: 'position', width: 80, sorter: (a, b) => a.position - b.position, render: (v) => Number(v).toFixed(1)},
	];

	const missingCols = [
		{title: 'Từ khoá (chưa có trang chuyên)', dataIndex: 'keyword', key: 'keyword', ellipsis: true},
		...metricCols,
		{
			title: '', key: 'action', align: 'right', width: 60,
			render: (_, r) => (
				<Tooltip title="Lưu vào kho từ khoá (để lên kế hoạch viết bài)">
					<Button small white disabled={saving} loading={savingKey === r.keyword} onClick={() => save(r)}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-bookmark" />} />
				</Tooltip>
			),
		},
	];

	const weakCols = [
		{
			title: 'Từ khoá → trang chủ đề', key: 'keyword', ellipsis: true,
			render: (_, r) => (
				<div>
					<strong>{r.keyword}</strong>
					<div style={{fontSize: 12.5, color: '#6b7280', wordBreak: 'break-all'}}>{r.url}</div>
				</div>
			),
		},
		{
			title: 'Link nội bộ', dataIndex: 'inlinks', key: 'inlinks', width: 110, align: 'center',
			sorter: (a, b) => a.inlinks - b.inlinks,
			render: (v) => <Tag color={v === 0 ? 'red' : 'orange'}>{v}</Tag>,
		},
		...metricCols,
	];

	return (
		<div>
			<p className={cn('muted')} style={{marginBottom: 12}}>
				Đối chiếu <strong>{fmt(data?.scanned)}</strong> truy vấn hàng đầu ({data?.range?.from} → {data?.range?.to})
				với {fmt(data?.crawl?.pages_crawled)} trang của lần crawl #{data?.crawl?.id}.
			</p>

			<div className="app-card" style={{marginBottom: 16}}>
				<h4 style={{margin: '0 0 4px'}}>
					<FontAwesomeIcon icon="fa-light fa-file-circle-plus" /> Thiếu bài viết
					<span style={{color: '#6b7280', fontWeight: 400}}> ({missing.length})</span>
				</h4>
				<p style={{fontSize: 13, color: '#6b7280', margin: '0 0 10px'}}>
					Google đang hiển thị site cho các truy vấn này nhưng KHÔNG trang nào có tiêu đề phủ đủ chủ đề —
					viết bài chuyên sâu riêng để đón nhu cầu.
				</p>
				<Table rowKey="keyword" size="small" columns={missingCols} dataSource={missing}
					locale={{emptyText: 'Không phát hiện chủ đề nào thiếu bài viết 🎉'}}
					pagination={{pageSize: 10, hideOnSinglePage: true}} scroll={{x: 'max-content'}} />
			</div>

			<div className="app-card">
				<h4 style={{margin: '0 0 4px'}}>
					<FontAwesomeIcon icon="fa-light fa-link-slash" /> Thiếu liên kết nội bộ
					<span style={{color: '#6b7280', fontWeight: 400}}> ({weakLink.length})</span>
				</h4>
				<p style={{fontSize: 13, color: '#6b7280', margin: '0 0 10px'}}>
					Trang chủ đề đã có nhưng ít trang khác trỏ tới (≤2 link nội bộ) — mở rộng hàng để xem
					gợi ý trang nguồn cùng chủ đề nên đặt link.
				</p>
				<Table rowKey="keyword" size="small" columns={weakCols} dataSource={weakLink}
					locale={{emptyText: 'Các trang chủ đề đều được liên kết nội bộ tốt 🎉'}}
					pagination={{pageSize: 10, hideOnSinglePage: true}} scroll={{x: 'max-content'}}
					expandable={{
						rowExpandable: (r) => (r.sources || []).length > 0,
						expandedRowRender: (r) => (
							<div>
								<strong style={{fontSize: 13}}>Trang nguồn gợi ý (đặt link trỏ tới trang chủ đề):</strong>
								{(r.sources || []).map((s) => (
									<div key={s.url} style={{fontSize: 13, margin: '4px 0', wordBreak: 'break-all'}}>
										<Tag>{s.inlinks} inlink</Tag> {s.title || s.url}
										<div style={{color: '#6b7280', fontSize: 12}}>{s.url}</div>
									</div>
								))}
							</div>
						),
					}} />
			</div>
		</div>
	);
}

export default KeywordSiteGap;
